import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Phone,
  Mail,
  MessageCircle,
  Facebook,
  Instagram,
  ArrowUpRight,
  Users,
  MapPin,
} from "lucide-react";

const Contact = () => {
  const handleLinkToSocialMedia = (link) => {
    window.open(link, "_blank");
  };

  const channels = [
    {
      onClick: () => handleLinkToSocialMedia("https://line.me/ti/p/%40postperty"),
      icon: <MessageCircle className="w-5 h-5" />,
      title: "Line Official",
      value: "@postperty",
      iconBg: "bg-emerald-50",
      iconColor: "text-emerald-600",
    },
    {
      onClick: () => handleLinkToSocialMedia("https://www.facebook.com/PostpertyDotcom"),
      icon: <Facebook className="w-5 h-5" />,
      title: "Facebook",
      value: "Postperty Fanpage",
      iconBg: "bg-blue-50",
      iconColor: "text-blue-600",
    },
    {
      onClick: () => handleLinkToSocialMedia("https://www.instagram.com/postperty/"),
      icon: <Instagram className="w-5 h-5" />,
      title: "Instagram",
      value: "@postperty",
      iconBg: "bg-pink-50",
      iconColor: "text-pink-600",
    },
    {
      onClick: () => window.open("tel:021233632"),
      icon: <Phone className="w-5 h-5" />,
      title: "Call Center",
      value: "02-123-3632",
      iconBg: "bg-gray-50",
      iconColor: "text-gray-600",
    },
    {
      onClick: () => window.open("tel:+66910920333"),
      icon: <Phone className="w-5 h-5" />,
      title: "Mobile",
      value: "091-092-0333",
      iconBg: "bg-sky-50",
      iconColor: "text-sky-600",
    },
    {
      onClick: () => window.open("mailto:contact@postperty.com"),
      icon: <Mail className="w-5 h-5" />,
      title: "Email",
      value: "contact@postperty.com",
      iconBg: "bg-gray-50",
      iconColor: "text-gray-600",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 py-8 lg:py-12 space-y-4 lg:pt-24">

        {/* ── Header card ── */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 px-5 py-4 flex items-center gap-3"
        >
          <Link to="/">
            <div className="w-9 h-9 bg-gray-50 rounded-xl flex items-center justify-center border border-gray-100 hover:bg-gray-100 transition-colors flex-shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </div>
          </Link>
          <img
            src="/branding/postperty-logo.png"
            className="w-10 h-10 rounded-xl object-contain flex-shrink-0 border border-gray-100"
            alt="Postperty Logo"
            onError={(e) => { e.target.style.display = "none"; }}
          />
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-0.5">ติดต่อเรา</p>
            <h1 className="text-lg font-bold text-gray-900">บริษัท โพสต์เพอร์ตี้ จำกัด</h1>
            <p className="text-xs text-gray-500">POSTPERTY CO., LTD.</p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

          {/* ── LEFT: Contact channels + Join team ── */}
          <div className="lg:col-span-2 space-y-4">

            {/* Channel cards */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.05, ease: "easeOut" }}
              className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
            >
              <div className="px-5 py-3.5 border-b border-gray-100">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">ช่องทางการติดต่อ</p>
              </div>
              <div className="divide-y divide-gray-50">
                {channels.map((ch, i) => (
                  <button
                    key={i}
                    onClick={ch.onClick}
                    className="w-full flex items-center gap-3.5 px-5 py-4 hover:bg-gray-50 active:bg-gray-50 transition-colors text-left"
                  >
                    <div className={`w-9 h-9 ${ch.iconBg} ${ch.iconColor} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      {ch.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-400 mb-0.5">{ch.title}</p>
                      <p className="text-sm font-semibold text-gray-800 truncate">{ch.value}</p>
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                  </button>
                ))}
              </div>
            </motion.div>

            {/* Join team card */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1, ease: "easeOut" }}
              className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex items-center gap-4"
            >
              <div className="w-11 h-11 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800">ร่วมงานกับเรา</p>
                <p className="text-xs text-gray-400 mt-0.5">ก้าวสู่ความสำเร็จในธุรกิจอสังหาฯ ไปด้วยกัน</p>
              </div>
              <Link to="/jointeam" className="flex-shrink-0">
                <div className="flex items-center gap-1.5 px-4 py-2.5 bg-blue-600 text-white text-xs font-semibold rounded-xl hover:bg-blue-700 transition-colors whitespace-nowrap">
                  สมัครร่วมงาน
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </div>
              </Link>
            </motion.div>
          </div>

          {/* ── RIGHT: Address + Map ── */}
          <div className="space-y-4">

            {/* Address card */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.08, ease: "easeOut" }}
              className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5"
            >
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-9 h-9 bg-gray-50 rounded-xl flex items-center justify-center border border-gray-100 flex-shrink-0">
                  <MapPin className="w-4.5 h-4.5 text-gray-500" />
                </div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">ที่อยู่</p>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                88/91 หมู่ 8 ต.บางแขม<br />
                อ.เมืองนครปฐม จ.นครปฐม 73000
              </p>
              <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-emerald-400" />
                  <span className="text-xs text-gray-400">พร้อมให้บริการ 24/7</span>
                </div>
              </div>
            </motion.div>

            {/* Map card */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.12, ease: "easeOut" }}
              className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
            >
              <div className="px-5 py-3.5 border-b border-gray-100">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">แผนที่</p>
              </div>
              <div className="h-64 lg:h-80">
                <iframe
                  title="POSTPERTY CO., LTD"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d242.1788285970681!2d100.03521695239088!3d13.78726510831891!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x30e2e7b5dfd2c139%3A0xa37e8248693e6df8!2sPOSTPERTY%20CO.%2C%20LTD.!5e0!3m2!1sth!2sth!4v1681114387115!5m2!1sth!2sth"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </motion.div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Contact;
